import type { DNRDatabase } from '@data/db/database';
import type { Setting } from '@data/db/types';
import { validateBackup, type BackupFile } from '@data/backup/backupFormat';
import { readDiskBackupReadOnly, type DiskBackupReadResult } from '@data/backup/localBackupEngine';
import type { DiskBackupTarget } from '@data/backup/diskBackupTarget';
import { journalSequencesOk, type JournalLine } from '@data/backup/localBackupFormat';
import { tableNameFor } from './applyChanges';
import { requireApprovedRepublishDevice, canonicalJson } from './republishEntities';
import { recordBusinessMutations, type RecordBusinessMutation } from './recordBusinessMutation';
import {
  SUPPORT_REPUBLISH_APPROVED_DEVICE_ID,
  SUPPORT_REPUBLISH_BUSINESS_ID,
  SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID,
  SUPPORT_REPUBLISH_DELETED_LINE_CHANGED_AT,
  SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT,
  SUPPORT_REPUBLISH_DELETED_LINE_ID,
  SUPPORT_REPUBLISH_DELETED_LINE_VERSION,
  SUPPORT_REPUBLISH_TARGETS,
  type RepublishTarget,
} from './republishEntities';
import type { SyncChange, SyncEntityType } from '@domain/sync/changeModel';
import {
  documentLineSchema,
  documentSchema,
  partySnapshotSchema,
} from '@domain/schemas/primitives';
import { isValidStateCode, stateNameFor } from '@core/gst/stateCodes';

export const LOCAL_EVIDENCE_RECOVERY_MARKER_KEY = 'support.local-evidence-recovery.entities';
export const LOCAL_EVIDENCE_RECOVERY_MARKER_SCOPE = 'business';

export type LocalEvidenceSource = 'backup-journal' | 'backup-checkpoint' | 'current-local-row';
export type LocalEvidenceOperation = 'UPSERT' | 'DELETE';

export interface LocalEvidenceRecordAssessment {
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly source: LocalEvidenceSource | null;
  readonly journalSequences: readonly number[];
  readonly originalChangeIds: readonly string[];
  readonly localRowAvailable: boolean;
  readonly operation: LocalEvidenceOperation | null;
  readonly version: number | null;
  readonly changedAt: string | null;
  readonly requiredFieldsValid: boolean;
  readonly relationshipsValid: boolean | null;
  readonly eligibility: 'PASS' | 'BLOCKED';
  readonly blockers: readonly string[];
}

export interface LocalEvidenceRecoveryItem {
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly operation: LocalEvidenceOperation;
  readonly version: number;
  readonly changedAt: string;
  readonly payload?: Record<string, unknown>;
  readonly deletedAt?: string;
  readonly source: LocalEvidenceSource;
  readonly journalSequences: readonly number[];
  readonly originalChangeIds: readonly string[];
}

export interface LocalEvidenceRecoveryPlan {
  readonly recoveryMode: 'LOCAL_EVIDENCE';
  readonly businessId: string;
  readonly sourceDeviceId: string;
  readonly deviceId: string;
  readonly targets: readonly RepublishTarget[];
  readonly evidenceFingerprint: string;
  readonly fingerprint: string;
  readonly markerId: string;
  readonly markerPresent: boolean;
  readonly records: readonly LocalEvidenceRecordAssessment[];
  readonly items: readonly LocalEvidenceRecoveryItem[];
  readonly blockers: readonly string[];
  readonly eligible: boolean;
  readonly acknowledgement: 'UNPROVEN';
}

export interface LocalEvidenceRecoveryResult {
  readonly changeIds: readonly string[];
  readonly fingerprint: string;
  readonly markerId: string;
  readonly itemCount: number;
}

const TABLE_BY_ENTITY: Partial<Record<SyncEntityType, string>> = {
  party: 'parties',
  product: 'products',
  document: 'documents',
  documentLine: 'documentLines',
};

const CHECKPOINT_TABLE_BY_ENTITY: Partial<Record<SyncEntityType, keyof BackupFile['tables']>> = {
  party: 'parties',
  product: 'products',
  document: 'documents',
  documentLine: 'documentLines',
};

const REQUIRED_TARGETS = SUPPORT_REPUBLISH_TARGETS;
const FIRST_PARTY_ID = REQUIRED_TARGETS.find((target) => target.entityType === 'party')?.entityId;
const PRODUCT_ID = REQUIRED_TARGETS.find((target) => target.entityType === 'product')?.entityId;
const INVOICE_ID = REQUIRED_TARGETS.find((target) => target.entityType === 'document')?.entityId;

interface RecoverySource {
  readonly target: RepublishTarget;
  readonly localRow?: Record<string, unknown>;
  readonly payload?: Record<string, unknown>;
  readonly source: LocalEvidenceSource | null;
  readonly operation: LocalEvidenceOperation | null;
  readonly version: number | null;
  readonly changedAt: string | null;
  readonly deletedAt?: string;
  readonly journalSequences: readonly number[];
  readonly originalChangeIds: readonly string[];
  readonly blockers: string[];
  requiredFieldsValid: boolean;
  relationshipsValid: boolean | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isTimestamp(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && !Number.isNaN(Date.parse(value));
}

function isPositiveVersion(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1;
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function markerId(businessId: string): string {
  return `${LOCAL_EVIDENCE_RECOVERY_MARKER_SCOPE}:${businessId}:${LOCAL_EVIDENCE_RECOVERY_MARKER_KEY}`;
}

function targetKey(target: RepublishTarget): string {
  return `${target.entityType}:${target.entityId}`;
}

function isTarget(target: RepublishTarget): boolean {
  return REQUIRED_TARGETS.some(
    (approved) =>
      approved.entityType === target.entityType && approved.entityId === target.entityId,
  );
}

function validateParty(payload: Record<string, unknown>): string[] {
  const errors: string[] = [];
  const requiredText = [
    'id',
    'businessId',
    'kind',
    'code',
    'name',
    'stateCode',
    'stateName',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
  ];
  for (const field of requiredText) {
    if (!hasText(payload[field]))
      errors.push(`party.${field} is missing or not a non-empty string`);
  }
  if (payload.kind !== 'CUSTOMER' && payload.kind !== 'SUPPLIER') {
    errors.push('party.kind must be CUSTOMER or SUPPLIER');
  }
  if (
    !Array.isArray(payload.billingAddressLines) ||
    !payload.billingAddressLines.every((value) => typeof value === 'string')
  ) {
    errors.push('party.billingAddressLines must be an array of strings');
  }
  if (
    payload.shippingAddressLines !== undefined &&
    (!Array.isArray(payload.shippingAddressLines) ||
      !payload.shippingAddressLines.every((value) => typeof value === 'string'))
  ) {
    errors.push('party.shippingAddressLines must be an array of strings when present');
  }
  if (!Array.isArray(payload.tags) || !payload.tags.every((value) => typeof value === 'string')) {
    errors.push('party.tags must be an array of strings');
  }
  if (!Number.isSafeInteger(payload.creditDays) || (payload.creditDays as number) < 0) {
    errors.push('party.creditDays must be a non-negative safe integer');
  }
  if (!Number.isSafeInteger(payload.openingBalancePaise)) {
    errors.push('party.openingBalancePaise must be a safe integer');
  }
  if (payload.creditLimitPaise !== undefined && !Number.isSafeInteger(payload.creditLimitPaise)) {
    errors.push('party.creditLimitPaise must be a safe integer when present');
  }
  if (typeof payload.isActive !== 'boolean') errors.push('party.isActive must be boolean');
  for (const field of [
    'gstin',
    'pan',
    'shippingStateCode',
    'shippingStateName',
    'phone',
    'email',
    'priceListId',
    'notes',
  ]) {
    if (payload[field] !== undefined && typeof payload[field] !== 'string') {
      errors.push(`party.${field} must be a string when present`);
    }
  }
  if (!isTimestamp(payload.createdAt)) errors.push('party.createdAt must be a parseable timestamp');
  if (!isTimestamp(payload.updatedAt)) errors.push('party.updatedAt must be a parseable timestamp');
  if (hasText(payload.stateCode) && !isValidStateCode(payload.stateCode)) {
    errors.push('party.stateCode is not in the application GST state table');
  } else if (
    hasText(payload.stateCode) &&
    hasText(payload.stateName) &&
    stateNameFor(payload.stateCode) !== payload.stateName
  ) {
    errors.push('party.stateName does not match the application GST state table for stateCode');
  }
  return errors;
}

function validateProduct(payload: Record<string, unknown>): string[] {
  const errors: string[] = [];
  for (const field of [
    'id',
    'businessId',
    'sku',
    'name',
    'unit',
    'taxRateId',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
  ]) {
    if (!hasText(payload[field]))
      errors.push(`product.${field} is missing or not a non-empty string`);
  }
  if (payload.kind !== 'GOODS' && payload.kind !== 'SERVICE') {
    errors.push('product.kind must be GOODS or SERVICE');
  }
  for (const field of [
    'unitPrecision',
    'conversionNumerator',
    'conversionDenominator',
    'cessRateBp',
    'salePricePaise',
    'purchasePricePaise',
  ]) {
    if (!Number.isSafeInteger(payload[field]))
      errors.push(`product.${field} must be a safe integer`);
  }
  for (const field of ['minStockMilli', 'reorderLevelMilli']) {
    if (payload[field] !== undefined && !Number.isSafeInteger(payload[field])) {
      errors.push(`product.${field} must be a safe integer when present`);
    }
  }
  for (const field of ['description', 'categoryId', 'hsnSac', 'secondaryUnit']) {
    if (payload[field] !== undefined && typeof payload[field] !== 'string') {
      errors.push(`product.${field} must be a string when present`);
    }
  }
  if (typeof payload.isStockTracked !== 'boolean')
    errors.push('product.isStockTracked must be boolean');
  if (typeof payload.isActive !== 'boolean') errors.push('product.isActive must be boolean');
  if (!isTimestamp(payload.createdAt))
    errors.push('product.createdAt must be a parseable timestamp');
  if (!isTimestamp(payload.updatedAt))
    errors.push('product.updatedAt must be a parseable timestamp');
  if (
    typeof payload.cessRateBp === 'number' &&
    (payload.cessRateBp < 0 || payload.cessRateBp > 10_000)
  ) {
    errors.push('product.cessRateBp must be between 0 and 10000');
  }
  return errors;
}

function validatePayload(target: RepublishTarget, payload: Record<string, unknown>): string[] {
  const errors: string[] = [];
  if (payload.id !== target.entityId)
    errors.push('payload.id does not match the approved entity ID');
  if (payload.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID) {
    errors.push('payload.businessId does not match the approved business ID');
  }
  if (payload.syncVersion !== undefined && !isPositiveVersion(payload.syncVersion)) {
    errors.push('payload.syncVersion must be a positive safe integer when present');
  }
  if (payload.syncChangeId !== undefined && !hasText(payload.syncChangeId)) {
    errors.push('payload.syncChangeId must be a non-empty string when present');
  }
  for (const field of ['syncUpdatedAt', 'syncDeletedAt']) {
    if (payload[field] !== undefined && !isTimestamp(payload[field])) {
      errors.push(`payload.${field} must be a parseable timestamp when present`);
    }
  }
  if (target.entityType === 'party') return errors.concat(validateParty(payload));
  if (target.entityType === 'product') return errors.concat(validateProduct(payload));
  const parsed =
    target.entityType === 'document'
      ? documentSchema.safeParse(payload)
      : target.entityType === 'documentLine'
        ? documentLineSchema.safeParse(payload)
        : null;
  if (!parsed) return errors.concat(`unsupported entity type ${target.entityType}`);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((issue) => {
      const path = issue.path.join('.');
      return `${path || target.entityType} ${issue.message}`;
    });
    errors.push(...issues);
  }
  return errors;
}

function validateBackupHeader(read: DiskBackupReadResult): string[] {
  const errors: string[] = [];
  if (!read.ok || !read.manifest || read.checkpoint === undefined) {
    return [read.message ?? 'The saved backup could not be read completely'];
  }
  if (read.journalTruncated) errors.push('The active journal has a truncated final line');
  const manifest = read.manifest;
  if (manifest.deviceId !== SUPPORT_REPUBLISH_APPROVED_DEVICE_ID) {
    errors.push('manifest.deviceId is not the approved original Edge device');
  }
  if (!manifest.businessIds.includes(SUPPORT_REPUBLISH_BUSINESS_ID)) {
    errors.push('manifest.businessIds does not include the approved business');
  }
  const validation = validateBackup(read.checkpoint);
  if (!validation.ok) {
    errors.push(validation.message ?? 'The active checkpoint is not a valid DNR backup');
    return errors;
  }
  const checkpoint = read.checkpoint as BackupFile;
  if (checkpoint.app !== 'DNR_Vyapar_2') {
    errors.push('checkpoint.app does not identify DNR Vyapar 2');
  }
  if (
    !Array.isArray(checkpoint.businessIds) ||
    !checkpoint.businessIds.includes(SUPPORT_REPUBLISH_BUSINESS_ID)
  ) {
    errors.push('checkpoint.businessIds does not include the approved business');
  }
  const businesses = checkpoint.tables.businesses;
  const businessExists =
    Array.isArray(businesses) &&
    businesses.some((row) => isRecord(row) && row.id === SUPPORT_REPUBLISH_BUSINESS_ID);
  if (!businessExists) errors.push('checkpoint has no approved business record');
  return errors;
}

function validateJournal(read: DiskBackupReadResult): {
  errors: string[];
  eventsByTarget: Map<string, Array<{ seq: number; change: SyncChange }>>;
} {
  const errors: string[] = [];
  const lines = read.journalLines;
  const manifest = read.manifest;
  const eventsByTarget = new Map<string, Array<{ seq: number; change: SyncChange }>>();
  if (!lines || !manifest) {
    return {
      errors: ['The backup reader did not provide sequence-numbered journal evidence'],
      eventsByTarget,
    };
  }
  const structurallyValidLines = lines.every(
    (line: unknown) => isRecord(line) && Number.isSafeInteger(line.seq) && isRecord(line.change),
  );
  if (!structurallyValidLines) {
    errors.push('Journal contains a malformed sequence line or change object');
  } else if (!journalSequencesOk(lines, manifest.checkpointSeq + 1)) {
    errors.push('Journal sequence numbers are missing, duplicated, or out of order');
  }
  const lastLine = lines.at(-1);
  const expectedNextSeq = lastLine ? lastLine.seq + 1 : manifest.checkpointSeq + 1;
  if (manifest.nextSeq !== expectedNextSeq) {
    errors.push('manifest.nextSeq does not follow the active journal tail');
  }
  if (manifest.pendingSinceCheckpoint !== lines.length) {
    errors.push('manifest.pendingSinceCheckpoint does not match the active journal length');
  }
  if (
    lastLine &&
    hasText(manifest.lastJournaledChangeId) &&
    isRecord(lastLine.change) &&
    manifest.lastJournaledChangeId !== lastLine.change.changeId
  ) {
    errors.push('manifest.lastJournaledChangeId does not match the active journal tail');
  }
  const changeIds = new Set<string>();
  const allowedEntities = new Set<SyncEntityType>([
    'party',
    'product',
    'category',
    'taxRate',
    'stockLocation',
    'document',
    'documentLine',
    'documentLink',
    'payment',
    'paymentAllocation',
    'expense',
    'stockTransaction',
    'business',
  ]);
  for (const rawLine of lines as readonly unknown[]) {
    if (!isRecord(rawLine)) continue;
    const line = rawLine as unknown as JournalLine;
    const change = line.change as SyncChange;
    if (!Number.isSafeInteger(line.seq) || line.seq < 1) {
      errors.push(`Journal sequence ${String(line.seq)} is invalid`);
      continue;
    }
    if (!isRecord(change)) {
      errors.push(`Journal sequence ${line.seq} has no change object`);
      continue;
    }
    if (!hasText(change.changeId) || changeIds.has(change.changeId)) {
      errors.push(`Journal sequence ${line.seq} has a missing or duplicate changeId`);
      continue;
    }
    changeIds.add(change.changeId);
    if (!hasText(change.businessId) || !manifest.businessIds.includes(change.businessId)) {
      errors.push(`Journal sequence ${line.seq} has a businessId outside the manifest`);
    }
    if (change.deviceId !== manifest.deviceId) {
      errors.push(`Journal sequence ${line.seq} deviceId does not match the backup manifest`);
    }
    if (!allowedEntities.has(change.entityType) || !hasText(change.entityId)) {
      errors.push(`Journal sequence ${line.seq} has an invalid entity identity`);
    }
    if (change.operation !== 'UPSERT' && change.operation !== 'DELETE') {
      errors.push(`Journal sequence ${line.seq} has an invalid operation`);
    }
    if (!isPositiveVersion(change.version)) {
      errors.push(`Journal sequence ${line.seq} has an invalid version`);
    }
    if (!isTimestamp(change.changedAt)) {
      errors.push(`Journal sequence ${line.seq} has an invalid changedAt`);
    }
    if (change.operation === 'UPSERT') {
      if (
        !isRecord(change.payload) ||
        change.payload.id !== change.entityId ||
        change.payload.businessId !== change.businessId
      ) {
        errors.push(`Journal sequence ${line.seq} UPSERT payload identity is invalid`);
      } else {
        if (
          change.payload.syncVersion !== undefined &&
          change.payload.syncVersion !== change.version
        ) {
          errors.push(
            `Journal sequence ${line.seq} payload syncVersion does not match event version`,
          );
        }
        if (
          change.payload.syncUpdatedAt !== undefined &&
          change.payload.syncUpdatedAt !== change.changedAt
        ) {
          errors.push(
            `Journal sequence ${line.seq} payload syncUpdatedAt does not match event changedAt`,
          );
        }
      }
    } else if (change.operation === 'DELETE') {
      if (change.payload !== undefined || !isTimestamp(change.deletedAt)) {
        errors.push(`Journal sequence ${line.seq} DELETE payload/deletedAt is invalid`);
      }
    }
    if (isTarget(change) && isRecord(change)) {
      const key = `${change.entityType}:${change.entityId}`;
      const targetEvents = eventsByTarget.get(key) ?? [];
      targetEvents.push({ seq: line.seq, change });
      eventsByTarget.set(key, targetEvents);
    }
  }
  return { errors, eventsByTarget };
}

function withoutSyncMetadata(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([key]) => !key.startsWith('sync')));
}

function checkpointRows(
  read: DiskBackupReadResult,
  target: RepublishTarget,
): Record<string, unknown>[] {
  const table = CHECKPOINT_TABLE_BY_ENTITY[target.entityType];
  if (!table || !isRecord(read.checkpoint) || !isRecord(read.checkpoint.tables)) return [];
  const rows = read.checkpoint.tables[table];
  return Array.isArray(rows)
    ? rows.filter(
        (row): row is Record<string, unknown> => isRecord(row) && row.id === target.entityId,
      )
    : [];
}

function makeSource(
  target: RepublishTarget,
  localRow: Record<string, unknown> | undefined,
  source: LocalEvidenceSource | null,
  payload: Record<string, unknown> | undefined,
  operation: LocalEvidenceOperation | null,
  version: number | null,
  changedAt: string | null,
  sequences: readonly number[],
  changeIds: readonly string[],
  blockers: string[] = [],
  deletedAt?: string,
): RecoverySource {
  return {
    target,
    localRow,
    payload,
    source,
    operation,
    version,
    changedAt,
    deletedAt,
    journalSequences: sequences,
    originalChangeIds: changeIds,
    blockers,
    requiredFieldsValid: false,
    relationshipsValid: null,
  };
}

async function loadSources(
  db: DNRDatabase,
  read: DiskBackupReadResult,
  eventsByTarget: Map<string, Array<{ seq: number; change: SyncChange }>>,
): Promise<RecoverySource[]> {
  const sources: RecoverySource[] = [];
  for (const target of REQUIRED_TARGETS) {
    const tableName = TABLE_BY_ENTITY[target.entityType] ?? tableNameFor(target.entityType);
    const localRow = tableName
      ? ((await db.table(tableName).get(target.entityId)) as Record<string, unknown> | undefined)
      : undefined;
    const events = eventsByTarget.get(targetKey(target)) ?? [];
    const sequences = events.map((event) => event.seq);
    const changeIds = events.map((event) => event.change.changeId);
    const isDeletedLine =
      target.entityType === 'documentLine' && target.entityId === SUPPORT_REPUBLISH_DELETED_LINE_ID;

    if (localRow && localRow.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID) {
      sources.push(
        makeSource(target, localRow, null, undefined, null, null, null, sequences, changeIds, [
          'The current Dexie row belongs to a different or missing business',
        ]),
      );
      continue;
    }

    if (isDeletedLine) {
      const upserts = events.filter((event) => event.change.operation === 'UPSERT');
      const deletes = events.filter((event) => event.change.operation === 'DELETE');
      const [upsert] = upserts;
      const [deletion] = deletes;
      const blockers: string[] = [];
      if (upserts.length !== 1 || deletes.length !== 1 || !upsert || !deletion) {
        blockers.push(
          'Deleted-line evidence must contain exactly one original UPSERT and one original DELETE',
        );
      } else {
        if (deletion.change.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID) {
          blockers.push('Deleted-line DELETE businessId does not match the approved business');
        }
        if (
          upsert.seq >= deletion.seq ||
          upsert.change.version !== 1 ||
          deletion.change.version !== SUPPORT_REPUBLISH_DELETED_LINE_VERSION
        ) {
          blockers.push(
            'Deleted-line UPSERT/DELETE sequence or versions are not the approved v1 then v2 history',
          );
        }
        if (
          deletion.change.changeId !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID ||
          deletion.change.changedAt !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGED_AT ||
          deletion.change.deletedAt !== SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT
        ) {
          blockers.push(
            'Deleted-line DELETE does not match its independently pinned original provenance',
          );
        }
        if (!isRecord(upsert.change.payload)) {
          blockers.push('Deleted-line original UPSERT payload is missing');
        }
      }
      if (
        localRow &&
        (localRow.isActive !== false ||
          localRow.syncVersion !== SUPPORT_REPUBLISH_DELETED_LINE_VERSION ||
          localRow.syncChangeId !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID ||
          localRow.syncDeletedAt !== SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT ||
          localRow.id !== SUPPORT_REPUBLISH_DELETED_LINE_ID ||
          localRow.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID)
      ) {
        blockers.push(
          'The current deleted-line tombstone metadata does not exactly match the original DELETE',
        );
      }
      const relationshipPayload =
        upsert && isRecord(upsert.change.payload) ? upsert.change.payload : undefined;
      const upsertPayloadErrors = relationshipPayload
        ? validatePayload(target, relationshipPayload)
        : ['Deleted-line original UPSERT payload is missing'];
      blockers.push(...upsertPayloadErrors);
      const deletedSource = makeSource(
        target,
        localRow,
        upsert && deletion ? 'backup-journal' : null,
        relationshipPayload,
        'DELETE',
        deletion?.change.version ?? null,
        deletion?.change.changedAt ?? null,
        sequences,
        changeIds,
        blockers,
        deletion?.change.deletedAt,
      );
      deletedSource.requiredFieldsValid = upsertPayloadErrors.length === 0;
      sources.push(deletedSource);
      continue;
    }

    const blockers: string[] = [];
    let payload: Record<string, unknown> | undefined;
    let source: LocalEvidenceSource | null = null;
    let operation: LocalEvidenceOperation | null = null;
    let version: number | null = null;
    let changedAt: string | null = null;
    let sourceEvents: Array<{ seq: number; change: SyncChange }> = [];
    if (events.length > 0) {
      const ordered = [...events].sort((a, b) => a.seq - b.seq);
      let priorVersion = 0;
      for (const event of ordered) {
        if (event.change.version <= priorVersion) {
          blockers.push('Target journal event versions are not strictly increasing');
          break;
        }
        priorVersion = event.change.version;
      }
      if (ordered.some((event) => event.change.operation === 'DELETE')) {
        blockers.push(
          'This target has a DELETE event and is not approved as a deleted recovery target',
        );
      }
      const latest = ordered[ordered.length - 1];
      if (latest?.change.operation !== 'UPSERT' || !isRecord(latest.change.payload)) {
        blockers.push('Latest target journal event is not a valid UPSERT with a payload');
      } else {
        payload = latest.change.payload;
        source = 'backup-journal';
        operation = 'UPSERT';
        version = latest.change.version;
        changedAt = latest.change.changedAt;
        sourceEvents = ordered;
      }
      if (localRow && payload) {
        const rowErrors = validatePayload(target, localRow);
        if (rowErrors.length > 0) {
          blockers.push(...rowErrors.map((error) => `current Dexie row: ${error}`));
        } else if (
          canonicalJson(withoutSyncMetadata(localRow)) !==
          canonicalJson(withoutSyncMetadata(payload))
        ) {
          blockers.push('Current Dexie row differs from the latest preserved UPSERT payload');
        }
      }
    } else {
      const fromCheckpoint = checkpointRows(read, target)[0];
      if (localRow) {
        payload = localRow;
        source = 'current-local-row';
      } else if (fromCheckpoint) {
        payload = fromCheckpoint;
        source = 'backup-checkpoint';
      } else {
        blockers.push('No matching journal event, checkpoint row, or current Dexie row was found');
      }
      operation = 'UPSERT';
      const versionValue = payload?.syncVersion;
      version = isPositiveVersion(versionValue) ? versionValue : null;
      const timestamp = payload?.syncUpdatedAt ?? payload?.updatedAt;
      changedAt = isTimestamp(timestamp) ? timestamp : null;
      if (version === null) blockers.push('No valid syncVersion is present in the selected source');
      if (changedAt === null)
        blockers.push('No valid syncUpdatedAt or updatedAt is present in the selected source');
    }

    const payloadErrors = payload
      ? validatePayload(target, payload)
      : ['Recovery payload is missing'];
    blockers.push(...payloadErrors);
    if (target.entityType === 'document' && payload) {
      if (
        payload.docNumber !== 'INV/2026-27/0001' ||
        payload.status !== 'ISSUED' ||
        version !== 3
      ) {
        blockers.push('Invoice source must be issued invoice INV/2026-27/0001 at version 3');
      }
    }
    if (target.entityType === 'documentLine' && payload && payload.isActive !== true) {
      blockers.push('The approved active invoice line source must have isActive === true');
    }
    if (!changedAt || !isTimestamp(changedAt))
      blockers.push('Recovery changedAt is unavailable or invalid');
    const sourceRecord = makeSource(
      target,
      localRow,
      source,
      payload,
      operation,
      version,
      changedAt,
      sourceEvents.map((event) => event.seq),
      sourceEvents.map((event) => event.change.changeId),
      blockers,
    );
    sourceRecord.requiredFieldsValid =
      payloadErrors.length === 0 &&
      version !== null &&
      Boolean(changedAt && isTimestamp(changedAt));
    sources.push(sourceRecord);
  }
  return sources;
}

function compareSnapshot(
  snapshotValue: unknown,
  party: Record<string, unknown> | undefined,
): string[] {
  if (!party)
    return ['The approved first-party payload is unavailable for invoice snapshot comparison'];
  if (!isRecord(snapshotValue)) return ['invoice.partySnapshot is missing or invalid'];
  const parsed = partySnapshotSchema.safeParse(snapshotValue);
  if (!parsed.success) {
    return parsed.error.issues.map(
      (issue) => `invoice.partySnapshot.${issue.path.join('.') || 'value'} ${issue.message}`,
    );
  }
  const fields = [
    'id',
    'name',
    'gstin',
    'stateCode',
    'stateName',
    'billingAddressLines',
    'phone',
    'email',
  ];
  const mismatches = fields.filter((field) => {
    const expectedField = field === 'billingAddressLines' ? 'addressLines' : field;
    return (
      canonicalJson(parsed.data[expectedField as keyof typeof parsed.data]) !==
      canonicalJson(party[field])
    );
  });
  return mismatches.map(
    (field) => `invoice.partySnapshot.${field} differs from the approved party source`,
  );
}

function validateRelationships(sources: RecoverySource[]): void {
  const byKey = new Map(sources.map((source) => [targetKey(source.target), source]));
  const invoiceTarget = REQUIRED_TARGETS.find((target) => target.entityType === 'document');
  const partyTarget = REQUIRED_TARGETS.find(
    (target) => target.entityType === 'party' && target.entityId === FIRST_PARTY_ID,
  );
  const productTarget = REQUIRED_TARGETS.find((target) => target.entityType === 'product');
  const invoice = invoiceTarget ? byKey.get(targetKey(invoiceTarget)) : undefined;
  const party = partyTarget ? byKey.get(targetKey(partyTarget)) : undefined;
  const product = productTarget ? byKey.get(targetKey(productTarget)) : undefined;
  let invoiceRelationshipsValid = false;
  if (invoice?.payload && party?.payload && invoiceTarget && partyTarget) {
    const customer = invoice.payload.customerId;
    const snapshotErrors = compareSnapshot(invoice.payload.partySnapshot, party.payload);
    if (customer !== partyTarget.entityId) {
      invoice.blockers.push('Invoice customerId does not reference the approved first party');
    }
    invoice.blockers.push(...snapshotErrors);
    invoiceRelationshipsValid = customer === partyTarget.entityId && snapshotErrors.length === 0;
  }
  if (invoice) invoice.relationshipsValid = invoiceRelationshipsValid;

  for (const source of sources.filter(
    (candidate) => candidate.target.entityType === 'documentLine',
  )) {
    const payload = source.payload;
    const deletedTarget = source.target.entityId === SUPPORT_REPUBLISH_DELETED_LINE_ID;
    if (!payload || !invoiceTarget || !productTarget || !invoice || !product) {
      source.blockers.push(
        'Required invoice, product, or line source is unavailable for relationship validation',
      );
      source.relationshipsValid = false;
      continue;
    }
    if (payload.documentId !== INVOICE_ID || payload.productId !== PRODUCT_ID) {
      source.blockers.push('Invoice line must reference the approved invoice and product');
    }
    if (invoice.payload && payload.docType !== invoice.payload.docType) {
      source.blockers.push('Invoice line docType does not match the approved invoice');
    }
    source.relationshipsValid =
      payload.documentId === INVOICE_ID &&
      payload.productId === PRODUCT_ID &&
      Boolean(invoice.payload) &&
      payload.docType === invoice.payload?.docType;
    if (deletedTarget && source.operation !== 'DELETE') {
      source.blockers.push('Deleted invoice line must be recovered only as DELETE');
      source.relationshipsValid = false;
    }
  }
  for (const source of sources) {
    if (source.target.entityType === 'party' || source.target.entityType === 'product') {
      source.relationshipsValid = null;
    }
  }
}

function evidenceFingerprint(
  read: DiskBackupReadResult,
  sources: readonly RecoverySource[],
): string {
  const checkpointRecord = isRecord(read.checkpoint) ? read.checkpoint : undefined;
  const checkpointTables = isRecord(checkpointRecord?.tables) ? checkpointRecord.tables : undefined;
  const checkpoint = checkpointTables
    ? Object.fromEntries(
        REQUIRED_TARGETS.map((target) => {
          const table = CHECKPOINT_TABLE_BY_ENTITY[target.entityType];
          const rows = table ? checkpointTables[table] : undefined;
          const match = Array.isArray(rows)
            ? rows.filter((row) => isRecord(row) && row.id === target.entityId)
            : [];
          return [targetKey(target), match];
        }),
      )
    : null;
  const targetJournalLines = (read.journalLines ?? []).filter((line) => {
    if (!isRecord(line) || !isRecord(line.change)) return false;
    const change = line.change;
    return REQUIRED_TARGETS.some(
      (target) => target.entityType === change.entityType && target.entityId === change.entityId,
    );
  });
  return canonicalJson({
    manifest: read.manifest,
    checkpoint: checkpoint,
    journalLines: targetJournalLines,
    currentRows: sources.map((source) => ({
      target: source.target,
      row: source.localRow,
    })),
    sourceItems: sources.map((source) => ({
      target: source.target,
      source: source.source,
      payload: source.payload,
      operation: source.operation,
      version: source.version,
      changedAt: source.changedAt,
      deletedAt: source.deletedAt,
      journalSequences: source.journalSequences,
      originalChangeIds: source.originalChangeIds,
      blockers: source.blockers,
    })),
  });
}

function buildPlan(
  read: DiskBackupReadResult,
  sources: RecoverySource[],
  deviceId: string,
  headerErrors: string[],
  journalErrors: string[],
  targetOutboxBlockers: ReadonlyMap<string, readonly string[]>,
): LocalEvidenceRecoveryPlan {
  const errors = [...headerErrors, ...journalErrors];
  const manifest = read.manifest;
  if (!manifest || manifest.deviceId !== SUPPORT_REPUBLISH_APPROVED_DEVICE_ID) {
    errors.push('The source backup does not identify the approved original Edge device');
  }
  const records = sources.map((source): LocalEvidenceRecordAssessment => {
    const outboxBlockers = targetOutboxBlockers.get(targetKey(source.target)) ?? [];
    const blockers = [...errors, ...source.blockers, ...outboxBlockers];
    return {
      entityType: source.target.entityType,
      entityId: source.target.entityId,
      source: source.source,
      journalSequences: source.journalSequences,
      originalChangeIds: source.originalChangeIds,
      localRowAvailable: Boolean(source.localRow),
      operation: source.operation,
      version: source.version,
      changedAt: source.changedAt,
      requiredFieldsValid: source.requiredFieldsValid,
      relationshipsValid: source.relationshipsValid,
      eligibility: blockers.length === 0 ? 'PASS' : 'BLOCKED',
      blockers,
    };
  });
  const items = sources
    .filter(
      (
        source,
      ): source is RecoverySource & {
        source: LocalEvidenceSource;
        operation: LocalEvidenceOperation;
        version: number;
        changedAt: string;
      } =>
        source.blockers.length === 0 &&
        source.source !== null &&
        source.operation !== null &&
        source.version !== null &&
        source.changedAt !== null,
    )
    .map((source): LocalEvidenceRecoveryItem => ({
      entityType: source.target.entityType,
      entityId: source.target.entityId,
      operation: source.operation,
      version: source.version,
      changedAt: source.changedAt,
      ...(source.operation === 'UPSERT' && source.payload ? { payload: source.payload } : {}),
      ...(source.operation === 'DELETE' && source.deletedAt ? { deletedAt: source.deletedAt } : {}),
      source: source.source,
      journalSequences: source.journalSequences,
      originalChangeIds: source.originalChangeIds,
    }));
  const sourceFingerprint = evidenceFingerprint(read, sources);
  const id = markerId(SUPPORT_REPUBLISH_BUSINESS_ID);
  const markerPresent = false;
  if (markerPresent) errors.push('A prior local-evidence recovery marker already exists');
  for (const record of records) {
    if (record.blockers.length > 0)
      errors.push(`${record.entityType} ${record.entityId}: ${record.blockers.join('; ')}`);
  }
  if (records.length !== REQUIRED_TARGETS.length || items.length !== REQUIRED_TARGETS.length) {
    errors.push('All eight approved targets must pass before any recovery event can be queued');
  }
  const fingerprint = canonicalJson({
    recoveryMode: 'LOCAL_EVIDENCE',
    businessId: SUPPORT_REPUBLISH_BUSINESS_ID,
    sourceDeviceId: manifest?.deviceId ?? null,
    deviceId,
    targets: REQUIRED_TARGETS,
    evidenceFingerprint: sourceFingerprint,
    records,
    items,
  });
  return {
    recoveryMode: 'LOCAL_EVIDENCE',
    businessId: SUPPORT_REPUBLISH_BUSINESS_ID,
    sourceDeviceId: manifest?.deviceId ?? '',
    deviceId,
    targets: REQUIRED_TARGETS,
    evidenceFingerprint: sourceFingerprint,
    fingerprint,
    markerId: id,
    markerPresent,
    records,
    items,
    blockers: errors,
    eligible: errors.length === 0,
    acknowledgement: 'UNPROVEN',
  };
}

async function planFromRead(
  db: DNRDatabase,
  read: DiskBackupReadResult,
): Promise<LocalEvidenceRecoveryPlan> {
  const deviceId = await requireApprovedRepublishDevice(db);
  const headerErrors = validateBackupHeader(read);
  const { errors: journalErrors, eventsByTarget } = validateJournal(read);
  const sources = await loadSources(db, read, eventsByTarget);
  validateRelationships(sources);
  const pendingOutbox = await db.syncOutbox
    .where('businessId')
    .equals(SUPPORT_REPUBLISH_BUSINESS_ID)
    .filter((entry) => entry.status === 'pending' || entry.status === 'failed')
    .toArray();
  const approvedTargetKeys = new Set(REQUIRED_TARGETS.map(targetKey));
  const targetOutboxBlockers = new Map<string, string[]>();
  for (const entry of pendingOutbox) {
    const key = `${entry.entityType}:${entry.entityId}`;
    if (!approvedTargetKeys.has(key)) continue;
    const blockers = targetOutboxBlockers.get(key) ?? [];
    blockers.push(
      entry.status === 'pending'
        ? 'An approved target already has a pending outbox event'
        : `An approved target has a failed outbox event (${entry.changeId}) that may conflict with recovery`,
    );
    targetOutboxBlockers.set(key, blockers);
  }
  const plan = buildPlan(
    read,
    sources,
    deviceId,
    headerErrors,
    journalErrors,
    targetOutboxBlockers,
  );
  const existingMarker = await db.settings.get(plan.markerId);
  if (!existingMarker) return plan;
  const markerBlocker = `A local-evidence recovery was already recorded at ${existingMarker.updatedAt}`;
  return {
    ...plan,
    markerPresent: true,
    eligible: false,
    records: plan.records.map((record) => ({
      ...record,
      eligibility: 'BLOCKED',
      blockers: [...record.blockers, markerBlocker],
    })),
    blockers: [...plan.blockers, markerBlocker],
  };
}

export async function previewLocalEvidenceRecovery(
  db: DNRDatabase,
  target: DiskBackupTarget,
): Promise<LocalEvidenceRecoveryPlan> {
  const read = await readDiskBackupReadOnly(target);
  return planFromRead(db, read);
}

export function formatLocalEvidenceRecoveryPlan(plan: LocalEvidenceRecoveryPlan): string {
  const lines = [
    'RECOVERY BASIS: preserved local Edge evidence; original cloud acknowledgement is UNPROVEN.',
    `Business: ${plan.businessId}`,
    `Source device: ${plan.sourceDeviceId || 'unverified'}`,
    '',
  ];
  for (const record of plan.records) {
    const evidenceDetails = [
      record.journalSequences.length ? `seq=${record.journalSequences.join(',')}` : null,
      record.originalChangeIds.length
        ? `originalChangeIds=${record.originalChangeIds.join(',')}`
        : null,
      record.changedAt ? `changedAt=${record.changedAt}` : null,
    ].filter((detail): detail is string => detail !== null);
    const relationshipStatus =
      record.relationshipsValid === null
        ? 'NOT_APPLICABLE'
        : record.relationshipsValid
          ? 'PASS'
          : 'FAIL';
    const sourceText = record.source ?? 'none';
    const operationText = record.operation
      ? `${record.operation} v${record.version ?? 'unavailable'}`
      : 'operation unresolved';
    lines.push(
      `${record.eligibility} ${record.entityType} ${record.entityId}: ${operationText}; ` +
        `source=${sourceText}; localRow=${record.localRowAvailable ? 'available' : 'absent'}; ` +
        `requiredFields=${record.requiredFieldsValid ? 'PASS' : 'FAIL'}; ` +
        `relationships=${relationshipStatus}${evidenceDetails.length ? `; ${evidenceDetails.join('; ')}` : ''}`,
    );
    for (const blocker of record.blockers) lines.push(`  BLOCKER: ${blocker}`);
  }
  if (plan.blockers.length > 0) {
    lines.push(
      '',
      'NO RECOVERY EVENTS CAN BE QUEUED:',
      ...plan.blockers.map((blocker) => `  ${blocker}`),
    );
  } else {
    lines.push('', 'All eight records passed source, schema, provenance, and relationship checks.');
  }
  return lines.join('\n');
}

export async function executeLocalEvidenceRecovery(
  db: DNRDatabase,
  target: DiskBackupTarget,
  plan: LocalEvidenceRecoveryPlan,
  now: string,
): Promise<LocalEvidenceRecoveryResult> {
  if (
    plan.recoveryMode !== 'LOCAL_EVIDENCE' ||
    !plan.eligible ||
    plan.acknowledgement !== 'UNPROVEN'
  ) {
    throw new Error('Local-evidence recovery is blocked; a complete eligible preview is required.');
  }
  if (
    plan.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID ||
    plan.targets.length !== REQUIRED_TARGETS.length ||
    plan.targets.some(
      (item, index) =>
        item.entityType !== REQUIRED_TARGETS[index]?.entityType ||
        item.entityId !== REQUIRED_TARGETS[index]?.entityId,
    )
  ) {
    throw new Error('The plan does not contain exactly the approved eight recovery targets.');
  }
  const queuedAt = Date.parse(now);
  if (!Number.isFinite(queuedAt)) throw new Error('Recovery execution timestamp must be valid.');

  const freshRead = await readDiskBackupReadOnly(target);
  const freshPreview = await planFromRead(db, freshRead);
  if (!freshPreview.eligible || canonicalJson(freshPreview) !== canonicalJson(plan)) {
    throw new Error(
      'Source evidence or local records changed after preview, or the supplied plan does not match the freshly validated preview; create and review a new preview.',
    );
  }

  const tableNames = new Set<string>();
  for (const item of freshPreview.items) {
    const tableName = tableNameFor(item.entityType);
    if (tableName) tableNames.add(tableName);
  }
  const tables = [...tableNames].map((name) => db.table(name));
  return db.transaction('rw', [db.syncOutbox, db.settings, ...tables], async () => {
    const executionDeviceId = await requireApprovedRepublishDevice(db);
    if (executionDeviceId !== freshPreview.deviceId) {
      throw new Error('The approved source device identity changed after preview.');
    }
    if (await db.settings.get(freshPreview.markerId)) {
      throw new Error('A prior local-evidence recovery is already recorded.');
    }
    const conflictingOutboxTargets = new Set<string>();
    const allOutbox = await db.syncOutbox.toArray();
    for (const entry of allOutbox) {
      if (
        entry.businessId === freshPreview.businessId &&
        (entry.status === 'pending' || entry.status === 'failed')
      ) {
        conflictingOutboxTargets.add(`${entry.entityType}:${entry.entityId}`);
      }
    }
    const conflicts = freshPreview.items.filter((item) =>
      conflictingOutboxTargets.has(targetKey(item)),
    );
    if (conflicts.length > 0) {
      throw new Error(
        `Recovery refused because approved targets already have pending or failed outbox entries: ${conflicts.map(targetKey).join(', ')}`,
      );
    }
    const revalidated = await planFromRead(db, freshRead);
    if (!revalidated.eligible || canonicalJson(revalidated) !== canonicalJson(freshPreview)) {
      throw new Error('Source evidence or local records changed before recovery could be queued.');
    }
    const mutations: RecordBusinessMutation[] = revalidated.items.map((item, index) => ({
      businessId: revalidated.businessId,
      deviceId: executionDeviceId,
      entityType: item.entityType,
      entityId: item.entityId,
      operation: item.operation,
      version: item.version,
      changedAt: item.changedAt,
      queuedAt: new Date(queuedAt + index).toISOString(),
      ...(item.payload ? { payload: item.payload } : {}),
      ...(item.deletedAt ? { deletedAt: item.deletedAt } : {}),
    }));
    const changeIds = await recordBusinessMutations(db, mutations);
    const marker: Setting = {
      id: revalidated.markerId,
      key: LOCAL_EVIDENCE_RECOVERY_MARKER_KEY,
      scope: LOCAL_EVIDENCE_RECOVERY_MARKER_SCOPE,
      scopeId: revalidated.businessId,
      businessId: revalidated.businessId,
      value: {
        mode: 'LOCAL_EVIDENCE',
        acknowledgement: 'UNPROVEN',
        fingerprint: revalidated.fingerprint,
        evidenceFingerprint: revalidated.evidenceFingerprint,
        changeIds,
        records: revalidated.items.map(
          (item) => `${item.entityType}:${item.entityId}@${item.version}:${item.operation}`,
        ),
        sourceDeviceId: revalidated.sourceDeviceId,
        queuedAt: now,
        deviceId: executionDeviceId,
      },
      updatedAt: now,
      updatedBy: executionDeviceId,
    };
    await db.settings.put(marker);
    return {
      changeIds,
      fingerprint: revalidated.fingerprint,
      markerId: revalidated.markerId,
      itemCount: revalidated.items.length,
    };
  });
}
